import {
  EntitiesMap,
  CompanyFollowing,
  Company,
  Post,
  PostCategory,
  PostState,
  CompanyValue,
  Image,
} from "../entities";
import { DraftPostPin } from "../reducers/posts";
import { requestCreator, UnknownError } from "../../common/actions/VisitApi";

export interface CompanyShowResponse {
  result: {
    company: EntityId;
    featured_memberships: EntityId[];
    company_membership_sections: EntityId[];
    projects: EntityId[];
    portfolio_projects: EntityId[];
    company_values: EntityId[];
    post_pins: EntityId[];
    latest_pinnable_posts: EntityId[];
  };
  entities: EntitiesMap;
}

export const requestToLoadCompanyShow = requestCreator<Company, CompanyShowResponse, Error>(p => ({
  path: `/companies/${p.id}.json`,
}));

export interface CompanyAboutResponse {
  result: {
    company: EntityId;
  };
  entities: EntitiesMap;
}

export const requestToLoadCompanyAbout = requestCreator<Company, CompanyAboutResponse, Error>(p => ({
  path: `/companies/${p.id}/about.json`,
}));

export interface CompanyMembersResponse {
  result: {
    company: EntityId;
    members: EntityId[];
    sections: EntityId[];
    memberships: EntityId[];
    featurings: EntityId[];
  };
  entities: EntitiesMap;
}

export const requestToLoadCompanyMembers = requestCreator<Company, CompanyMembersResponse, Error>(p => ({
  path: `/companies/${p.id}/members.json`,
}));

export interface CompanyPostsResponse {
  result: {
    company: EntityId;
    posts: EntityId[];
    post_pins: EntityId[];
    latest_pinnable_posts: EntityId[];
    spaces: EntityId[];
  };
  entities: EntitiesMap;
}

export const requestToLoadCompanyPosts = requestCreator<Company, CompanyPostsResponse, Error>(p => ({
  path: `/companies/${p.id}/feed.json`,
}));

export interface CompanyProjectsResponse {
  result: {
    company: EntityId;
    projects: EntityId[];
  };
  entities: EntitiesMap;
}

export const requestToLoadCompanyProjects = requestCreator<Company, CompanyProjectsResponse, Error>(p => ({
  path: `/companies/${p.id}/projects.json`,
}));

export interface SimilarCompaniesResponse {
  result: EntityId[];
  entities: EntitiesMap;
}
export const requestToLoadSimilarCompanies = requestCreator<Company, SimilarCompaniesResponse, Error>(p => {
  const include = ["background", "avatar"];
  const fields = ["name", "company_name", "short_location", "mission_statement", "background.url", "avatar.url"];
  return {
    path: `/api/v2/companies/${p.id}/similar_companies.json?include=${include.join()}&fields=${fields}`,
  };
});

export const requestToLikePost = requestCreator<Post, null, UnknownError>(p => ({
  path: `/api/internal/posts/${p.id}/like`,
  method: "POST",
}));

export const requestToUnlikePost = requestCreator<Post, null, UnknownError>(p => ({
  path: `/api/internal/posts/${p.id}/like`,
  method: "DELETE",
}));

export interface UpdateCompanyResponse {
  result: EntityId;
  entities: EntitiesMap;
}

export const requestToUpdateCompany = requestCreator<
  { company: Company; avatar?: Image; background?: Image },
  UpdateCompanyResponse,
  UnknownError
>(({ company, avatar, background }) => {
  const body: any = { ...company };
  if (avatar) {
    body.avatar_attributes = { id: avatar.id, import_url: avatar.url };
  }
  if (background) {
    body.background_attributes = { id: background.id, import_url: background.url };
  }
  return {
    path: `/api/v2/companies/${company.id}`,
    method: "PUT",
    query: {
      fields: [
        "id",
        "name",
        "company_name",
        "company_path",
        "font_color_code",
        "short_location",
        "address_prefix",
        "address_suffix",
        "latitude",
        "longitude",
        "url",
        "founded_on",
        "founder",
        "origin",
        "mission_statement",
        "font_color_code",
        "misc_tag_names",
        "payroll_number",
        "policy_update",
        "is_following",
        "avatar.id",
        "avatar.url",
        "background.id",
        "background.url",
      ].join(","),
      include: ["avatar", "background"].join(","),
    },
    body,
  };
});

export interface UpdateCompanyValuesResponse {
  result: EntityId[];
  entities: EntitiesMap;
}

// TODO(KentoMoriwaki): Migrate to v2
export const requestToUpdatePostPins = requestCreator<DraftPostPin, null, UnknownError>(pin => ({
  path: "/api/internal/company_post_pins",
  method: "POST",
  body: {
    company_id: pin.company,
    post_id: pin.post,
    category_cd: pin.category_cd,
  },
}));

type CompanyPostPinsResponse = Array<{
  pin_id: number | null;
  category_cd: number;
  post: { id: number };
}>;

// TODO(KentoMoriwaki): Migrate to v2
export const requestToLoadPostPins = requestCreator<{ companyId: EntityId }, CompanyPostPinsResponse, UnknownError>(
  ({ companyId }) => ({
    path: "/api/internal/company_post_pins",
    query: {
      company_id: companyId,
    },
  })
);

export const requestToUpdateCompanyValues = requestCreator<
  { company: Company; companyValues: CompanyValue[] },
  UpdateCompanyValuesResponse,
  Error
>(params => ({
  path: `/api/v2/companies/${params.company.id}/company_values`,
  method: "PUT",
  query: {
    fields: [
      "feature",
      "feature_cd",
      "feature_color",
      "feature_title",
      "feature_title_with_break",
      "description",
      "rank",
    ].join(","),
    include: [].join(","),
  },
  body: {
    company_values: params.companyValues,
  },
}));

export const requestToFollowCompany = requestCreator<Company, CompanyFollowing, UnknownError>(p => ({
  path: `/api/v2/company_followings`,
  method: "POST",
  body: {
    company_id: p.id,
  },
}));

export const requestToUnfollowCompany = requestCreator<Company, null, UnknownError>(p => ({
  path: `/api/v2/company_followings`,
  method: "DELETE",
  body: {
    company_id: p.id,
  },
}));

export const requestToLoadPosts = requestCreator<
  { companyId: string; page: number },
  { result: EntityId[]; entities: EntitiesMap },
  Error
>(params => ({
  path: `/api/v2/companies/${params.companyId}/posts`,
  query: {
    company_id: params.companyId,
    fields: "id,post_path,title,description_head,image.id,image.url,liked_status.state",
    include: "image,liked_status",
    page: params.page,
    per_page: 15,
    page_count: true,
    sort: "-published_at",
  },
  method: "GET",
}));

export const requestToLoadPinnablePosts = requestCreator<
  { companyId: string },
  { result: EntityId[]; entities: EntitiesMap },
  Error
>(params => ({
  path: `/api/v2/posts/pinnable_posts`,
  query: {
    target: "company",
    target_id: params.companyId,
    fields: ["id", "post_path", "title", "image.id", "image.url"].join(","),
    include: "image",
    sort: "-published_at",
  },
}));

export interface LoadInvisibleCompanyValuesResponse {
  result: EntityId[];
  entities: EntitiesMap;
}

export const requestToLoadInvisibleCompanyValues = requestCreator<
  { companyId: string },
  LoadInvisibleCompanyValuesResponse,
  Error
>(params => ({
  path: "/api/v2/company_values/invisibles",
  query: {
    company_id: params.companyId,
    fields: ["id", "feature_title", "feature_title_with_break", "feature", "description", "rank", "feature_color"].join(
      ","
    ),
  },
  method: "GET",
}));

export interface CreateCompanyPostPayload {
  post: Post;
  postImage?: Image;
}

interface CreateCompanyPostBody {
  company_id: string;
  user_id: string;
  description: string;
  category: PostCategory;
  state: PostState;
  internal: boolean;
  image_attributes?: {
    import_url: string;
  };
}

export const requestToSendCompanyPost = requestCreator<
  CreateCompanyPostPayload,
  { result: EntityId; entities: EntitiesMap },
  UnknownError
>(p => {
  const { post, postImage } = p;

  let body: CreateCompanyPostBody = {
    company_id: post.company!,
    user_id: post.user!,
    description: post.description_head!,
    category: "feed", // TODO: Enable to change category
    state: "published",
    internal: false,
  };

  if (typeof postImage !== "undefined") {
    body = { ...body, image_attributes: { import_url: postImage.url } };
  }

  return {
    path: "/api/v2/posts",
    query: {
      fields: "id,post_path,title,description_head,image.id,image.url",
      include: "image",
    },
    method: "POST",
    body,
  };
});



// WEBPACK FOOTER //
// ./frontend/src/companies/api/index.ts